import { useState, useRef, useEffect, useLayoutEffect, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';

export interface ActionMenuItem {
  /** Identificador único do item dentro do menu */
  key: string;
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  /** 'danger' aplica o tom vermelho para ações destrutivas */
  variant?: 'default' | 'danger';
  disabled?: boolean;
  /** Oculta o item (ex.: por permissão ou status) sem precisar filtrar a lista */
  hidden?: boolean;
  /** Desenha um divisor antes deste item (suprimido se for o primeiro visível) */
  dividerBefore?: boolean;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  /** Rótulo acessível e tooltip do botão gatilho */
  ariaLabel?: string;
  /** Largura do painel flutuante em px */
  width?: number;
}

/**
 * Menu de "Ações" por linha/contexto: um gatilho (três pontos) que abre um painel
 * flutuante com várias ações em um clique. Renderiza via portal com posição fixa
 * para não ser cortado por containers com overflow (ex.: DataTable) e fecha em
 * clique-fora, Escape, scroll de página ou resize. Inverte para cima quando há pouco
 * espaço abaixo e implementa navegação por teclado (setas / Home / End / Esc / Tab).
 */
export function ActionMenu({ items, ariaLabel = 'Ações', width = 240 }: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();

  const visibleItems = items.filter((i) => !i.hidden);
  const enabledIndices = visibleItems.reduce<number[]>((acc, item, i) => {
    if (!item.disabled) acc.push(i);
    return acc;
  }, []);

  // Calcula a posição (viewport) ao abrir, com flip vertical e clamp horizontal.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const estHeight = Math.min(visibleItems.length * 40 + 16, 360);
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < estHeight + 8 && rect.top > spaceBelow;
    const top = openUp ? Math.max(8, rect.top - estHeight - 4) : rect.bottom + 4;
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
    setCoords({ top, left });
  }, [open, visibleItems.length, width]);

  // Ao abrir, move o foco para o primeiro item habilitado (após o portal renderizar).
  useEffect(() => {
    if (!open) {
      setActiveIndex(-1);
      return;
    }
    const raf = requestAnimationFrame(() => {
      const first = visibleItems.findIndex((i) => !i.disabled);
      if (first >= 0) {
        setActiveIndex(first);
        itemRefs.current[first]?.focus();
      }
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fecha em interações externas. Listeners só ativos enquanto aberto.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScroll = (e: Event) => {
      // Ignora o scroll interno do próprio menu (lista que ultrapassa a altura máxima)
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const closeAndRestoreFocus = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const moveFocus = (dir: 1 | -1) => {
    if (enabledIndices.length === 0) return;
    const pos = enabledIndices.indexOf(activeIndex);
    let nextPos = pos + dir;
    if (nextPos < 0) nextPos = enabledIndices.length - 1;
    if (nextPos >= enabledIndices.length) nextPos = 0;
    const next = enabledIndices[nextPos];
    setActiveIndex(next);
    itemRefs.current[next]?.focus();
  };

  const focusEdge = (edge: 'first' | 'last') => {
    if (enabledIndices.length === 0) return;
    const next = edge === 'first' ? enabledIndices[0] : enabledIndices[enabledIndices.length - 1];
    setActiveIndex(next);
    itemRefs.current[next]?.focus();
  };

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); moveFocus(1); break;
      case 'ArrowUp': e.preventDefault(); moveFocus(-1); break;
      case 'Home': e.preventDefault(); focusEdge('first'); break;
      case 'End': e.preventDefault(); focusEdge('last'); break;
      case 'Escape': e.preventDefault(); closeAndRestoreFocus(); break;
      case 'Tab': e.preventDefault(); closeAndRestoreFocus(); break;
    }
  };

  if (visibleItems.length === 0) return null;

  return (
    <div className="inline-flex">
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        title={ariaLabel}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {open &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={ariaLabel}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={onMenuKeyDown}
            style={{ position: 'fixed', top: coords.top, left: coords.left, width }}
            className="z-[60] max-h-[360px] overflow-y-auto rounded-2xl border border-slate-200/70 bg-white py-1 shadow-[0_10px_40px_-12px_rgba(15,23,42,0.25)]"
          >
            {visibleItems.map((item, idx) => (
              <div key={item.key}>
                {item.dividerBefore && idx > 0 && <div className="my-1 h-px bg-slate-100" />}
                <button
                  ref={(el) => { itemRefs.current[idx] = el; }}
                  type="button"
                  role="menuitem"
                  tabIndex={activeIndex === idx ? 0 : -1}
                  disabled={item.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    item.onSelect();
                  }}
                  className={`flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset disabled:cursor-not-allowed disabled:opacity-50 ${
                    item.variant === 'danger'
                      ? 'text-rose-600 hover:bg-rose-50 focus-visible:ring-rose-500'
                      : 'text-slate-700 hover:bg-slate-50 focus-visible:ring-primary-500'
                  }`}
                >
                  {item.icon && (
                    <span className={`flex-shrink-0 ${item.variant === 'danger' ? '' : 'text-slate-400'}`}>
                      {item.icon}
                    </span>
                  )}
                  <span className="truncate">{item.label}</span>
                </button>
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
